import { connect, ConnectedProps } from 'react-redux'
import SellReadOnlyHeaderAccounts from '../../../../../headers/SellReadOnlyHeaderAccounts'
import SellReadOnlyHeaderCosts from '../../../../../headers/SellReadOnlyHeaderCosts'
import SellReadOnlyHeaderFinancialRows from '../../../../../headers/SellReadOnlyHeaderFinancialRows'
import SellStepHeaderMainNoPermission, { useSellStepHeaderMainNoPermissionLogic } from '../../../../../headers/SellStepHeaderMainNoPermission'
import SellStepHeaderShippingNoPermission from '../../../../../headers/SellStepHeaderShippingNoPermission'
import InputTableLeftColumn, { InputTableLeftColumnCompleteRequiredProps } from '../../../../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn'
import withLoading from '../../../../../../../../../../../utils/hoc/withLoading'
import useSellInvoiceHeaderUrls from '../../../../../../../../../../../utils/hooks/sells/orders/useSellInvoiceHeaderUrls'
import { ComponentTabObject } from '../../../../../../../../../../../utils/hooks/useTabLogic'
import useTwinTranslation from '../../../../../../../../../../../utils/hooks/useTwinTranslation'
import { AllReduxPayloads } from '../../../../../../../../../../../utils/reducers'
import { CostSellPayload } from '../../../../../../../../../../../utils/reducers/company/costsSell'
import { SellTagsPayload } from '../../../../../../../../../../../utils/reducers/sells/sellTags'
import { SellVariablePayload } from '../../../../../../../../../../../utils/reducers/company/sellVariable'
import SellReadOnlyHeaderVariables from '../../../../../headers/SellReadOnlyHeaderVariables'
import AppStyleDecider from '../../../../../../../../../../../baseComponents/AppsStyleDecider'
import { ITHeaderOrderTagsNew } from '../../../../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/Components'
import ITHeaderText from '../../../../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/Components/ITHeaderText'
import InputTableTopHeader from '../../../../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableTopHeader'
import { FormElement, FormRender } from '../../../../../../../../../../../forms/FormRenderer/types'
import { displayDate } from '../../../../../../../../../../../utils/globals/date'
import { returnClassOfSpanTemplate } from '../../../../../../../../../../../utils/globals/tailwind'
import { SellModelType } from '../../../../../headers/types'

type SellInvoiceCreateNoPermissionStepITHeaderProps = InputTableLeftColumnCompleteRequiredProps & ReduxSellInvoiceCreateNoPermissionStepITHeader & {}

const SellInvoiceCreateNoPermissionStepITHeader: React.FC<SellInvoiceCreateNoPermissionStepITHeaderProps> = ({ ...rest }) => {
    return <AppStyleDecider DefaultComponent={SellInvoiceCreateNoPermissionStepITHeaderDefault} NewComponent={SellInvoiceCreateNoPermissionStepITHeaderNew} {...rest} />
}

const SellInvoiceCreateNoPermissionStepITHeaderDefault: React.FC<SellInvoiceCreateNoPermissionStepITHeaderProps> = ({ sellTags, ...rest }) => {
    const { t } = useTwinTranslation()
    const orderHeaderURLs = useSellInvoiceHeaderUrls()
    const tabs: ComponentTabObject = {
        'main': {
            component: SellStepHeaderMainNoPermission,
            text: t('dataInvoice', 'Datos de la factura'),
            extraProps: { sellTags: sellTags?.sell }
        },
    }
    if (!rest.headerData?.hidePrices) {
        tabs['financialRow'] = {
            component: SellReadOnlyHeaderFinancialRows,
            text: t('discountsAndRecharges', 'Descuentos y recargos'),
            permission: 'sell.invoice',
            extraProps: { orderHeaderURLs }
        }
        tabs['costs'] = {
            component: SellReadOnlyHeaderCosts,
            text: t('costsInvoice', 'Gastos de la factura'),
            permission: 'sell.invoice',
            extraProps: { orderHeaderURLs }
        }
    }
    tabs['shipping'] = {
        component: SellStepHeaderShippingNoPermission,
        text: t('shipping', 'Transporte'),
        permission: 'sell.invoice',
        extraProps: {
            orderHeaderURLs
        }
    }
    tabs['accounts'] = {
        component: SellReadOnlyHeaderAccounts,
        text: t('divideIntoChapters', 'Dividir en capítulos'),
        permission: 'sell.invoice',
        extraProps: { orderHeaderURLs }
    }
    tabs['variables'] = {
        component: SellReadOnlyHeaderVariables,
        text: t('sellVariables', 'Variables de venta'),
        permission: 'sell.invoice',
        extraProps: { orderHeaderURLs }
    }
    return (
        <InputTableLeftColumn tabs={tabs} {...rest} />
    )
}

const SellInvoiceCreateNoPermissionStepITHeaderNew: React.FC<SellInvoiceCreateNoPermissionStepITHeaderProps> = ({ sellTags, setCompleteData, headerData }) => {
    const { t } = useTwinTranslation()
    const { memorizedAddresses, customerAddresses, activeCurrencies } = useSellStepHeaderMainNoPermissionLogic({ CustomerAddress: headerData?.CustomerAddress || null, WorkingCenterAddress: headerData?.WorkingCenterAddress || null, CustomerId: headerData?.CustomerId })
    let extraFields: FormElement<keyof SellModelType>[] = []
    if (headerData?.CustomerAddressId) {
        extraFields.push({
            name: 'CustomerAddressId',
            component: 'Custom',
            render: ({ value }) => {
                return <ITHeaderText label={t('fiscalAddress', 'Dirección Fiscal')} value={memorizedAddresses.customerAddress} />
            },
            extraProps: {
                customerAddress: memorizedAddresses.customerAddress
            },
            hideSectionByDefault: true
        })
    } else if (customerAddresses) {
        extraFields.push({
            name: 'CustomerAddressId',
            component: 'Custom',
            render: ({ value }) => {
                return <ITHeaderText label={t('fiscalAddress', 'Dirección Fiscal')} value={customerAddresses?.[headerData?.CustomerId]} />
            },
            extraProps: {
                customerAddresses
            },
            hideSectionByDefault: true
        },)
    }
    if (headerData?.WorkingCenterAddressId) {
        extraFields.push({
            name: 'WorkingCenterAddressId',
            component: 'Custom',
            render: ({ value, extraProps }) => {
                return <ITHeaderText label={t('workingCenterAddress', 'Dirección centro de trabajo')} value={memorizedAddresses.workingCenterAddress} />
            },
            extraProps: {
                workingCenterAddress: memorizedAddresses.workingCenterAddress
            },
            hideSectionByDefault: true
        },)
    }
    const sections: FormRender<SellModelType> = [
        {
            cols: 7,
            className: 'gap-x-20',
            elements: [
                {
                    name: 'full_assigned_number',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('assignedN', 'Nº asignado')} value={value} className={(headerData?.assigned_number ? '' : 'text-red-BA')} />,
                    hideSectionByDefault: true
                },
                {
                    name: 'processed_date',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('date', 'Fecha')} className='justify-center' value={displayDate(new Date(value))} />
                },
                {
                    name: 'RepresentativeEmployeeId',
                    component: 'Custom',
                    render: () => <ITHeaderText label={t('representative', 'Representante')} className='justify-center' value={headerData?.RepresentativeEmployee?.fullname_short} />
                },
                {
                    name: 'CustomerId',
                    component: 'Custom',
                    render: () => <ITHeaderText label={t('customer', 'Cliente')} value={headerData?.Customer?.fiscal_name || headerData?.Customer?.name} />,
                },
                {
                    name: 'OrderTags',
                    component: 'Custom',
                    render: () => {
                        return (
                            <ITHeaderOrderTagsNew tagType={1} readOnly={true} dictTags={sellTags?.sell || []} className={returnClassOfSpanTemplate(3) + ' order_top_header_custom_select_tags'} selectedTags={headerData?.tags || []} onChange={async () => { }} headerData={headerData || {}} />)
                    },
                },
                ...extraFields,
                {
                    name: 'WorkingCenterId',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('soldBy', 'Vendido por')} value={headerData?.WorkingCenter.name || ''} />,
                    hideSectionByDefault: true
                },
                {
                    name: 'WorkingCenterCounterId',
                    component: 'Custom',
                    render: ({ value }) => {
                        return <ITHeaderText label={t('serie', 'Serie')} value={headerData?.WorkingCenterCounter?.serie || ''} />
                    },
                    hideSectionByDefault: true
                },
                {
                    name: 'CurrencyId',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('currency', 'Moneda')} value={activeCurrencies?.[value]?.name} />,
                    hideSectionByDefault: true
                },
            ]
        }
    ]

    return (
        <InputTableTopHeader handleChange={async () => { }} sections={sections} headerData={headerData} getCompleteData={() => { }} />
    )
}

const SellInvoiceCreateNoPermissionStepITHeaderDispatch = {
    setCostsSell: (payload: CostSellPayload) => ({ type: 'CHANGE_COSTSELL', payload }),
    setSellTags: (payload: SellTagsPayload) => ({ type: 'CHANGE_SELL_TAGS', payload }),
    setSellVariables: (payload: SellVariablePayload) => ({ type: 'CHANGE_SELLVARIABLE', payload }),
}

export type ReduxSellInvoiceCreateNoPermissionStepITHeader = ConnectedProps<typeof sellInvoiceCreateStepHeaderConnect>
const mapSellInvoiceCreateStepHeaderConnector = (state: AllReduxPayloads) => ({ costsSell: state.costsSell, sellTags: state.sellTags, sellVariables: state.sellVariables })
const sellInvoiceCreateStepHeaderConnect = connect(mapSellInvoiceCreateStepHeaderConnector, SellInvoiceCreateNoPermissionStepITHeaderDispatch)
const sellInvoiceCreateStepHeaderConnectLoading = withLoading(SellInvoiceCreateNoPermissionStepITHeader, [{ fetchUrl: '/api/app/config/cost/costSell/getAllCostSells', propName: 'costsSell', setFunctionName: 'setCostsSell' }, { fetchUrl: '/api/app/order/sell/sellTag/getAllSellTags', propName: 'sellTags', setFunctionName: 'setSellTags' }, { fetchUrl: '/api/app/config/sell/sellVariable/getAllSellVariables', propName: 'sellVariables', setFunctionName: 'setSellVariables' }])
const SellInvoiceCreateNoPermissionStepITHeaderConnect = sellInvoiceCreateStepHeaderConnect(sellInvoiceCreateStepHeaderConnectLoading)

export default SellInvoiceCreateNoPermissionStepITHeaderConnect